import { useMemo } from 'react';
import { Box, Stack, Typography, LinearProgress, CircularProgress, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BuildIcon from '@mui/icons-material/Build';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectDashboard } from '../../../api/hooks/useProjectDashboard';
import { useProject } from '../../../api/hooks/useProjects';
import { PmCard, PmInfoBox, PmPill, type PmStep } from '../../../components/pm';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildDashboardReport } from '../reports/dashboardReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { DashboardStepIndicators } from '../components/dashboard/DashboardStepIndicators';
import { DashboardWorkChart } from '../components/dashboard/DashboardWorkChart';
import { DashboardMiniGantt } from '../components/dashboard/DashboardMiniGantt';
import { DashboardMilestones } from '../components/dashboard/DashboardMilestones';

interface DashboardKpi {
  rActive?: number;
  opOpen?: number;
}

interface ReifegradSchritt {
  schritt: number;
  reifegrad?: number | null;
  eskalation?: string | null;
  notes?: string | null;
}

interface WeeklyProgressEntry {
  kw?: number;
  jahr?: number;
  erledigt?: number;
  offen?: number;
}

interface DashboardData {
  kpi?: DashboardKpi | null;
  schritte?: ReifegradSchritt[] | null;
  weeklyProgress?: WeeklyProgressEntry[] | null;
  milestones?: unknown[] | null;
  miniGantt?: unknown[] | null;
}

const STEPS: Array<{ number: number; key: string; label: string }> = [
  { number: 1, key: 'machbarkeit', label: 'Machbarkeit' },
  { number: 2, key: 'rahmen', label: 'Rahmen' },
  { number: 3, key: 'projektauftrag', label: 'Projektauftrag' },
  { number: 4, key: 'ziele', label: 'Ziele' },
  { number: 5, key: 'zeiten', label: 'Zeiten' },
  { number: 6, key: 'team', label: 'Team' },
  { number: 7, key: 'unsicherheiten', label: 'Unsicherheiten' },
  { number: 8, key: 'anforderungen', label: 'Anforderungen' },
  { number: 9, key: 'scope', label: 'Scope' },
  { number: 10, key: 'arbeitspakete', label: 'Arbeitspakete' },
  { number: 11, key: 'projektplan', label: 'Projektplan' },
  { number: 12, key: 'risiken', label: 'Risiken' },
  { number: 13, key: 'offene-punkte', label: 'Offene Punkte' },
  { number: 14, key: 'kanban', label: 'Kanban' },
  { number: 15, key: 'burnup', label: 'Burn-Up' },
  { number: 16, key: 'status', label: 'Status' },
  { number: 17, key: 'retro', label: 'Retro' },
];

function ProgressBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-base)', fontWeight: 600 }}>
          {value} / {total}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          background: 'var(--pm-bg-deep)',
          '& .MuiLinearProgress-bar': { background: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
}

/** Card-Kopf im Clickdummy-Stil (Dot + Titel). */
function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--rittec-petrol)' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
      </Box>
      {action}
    </Box>
  );
}

export function DashboardTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const { data, isLoading } = useProjectDashboard(projectId);
  const { data: projectResp } = useProject(projectId);

  const dash = (data as unknown as { data?: DashboardData } | undefined)?.data ?? null;
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const reifegradMap = useMemo(() => {
    const map = new Map<number, ReifegradSchritt>();
    (dash?.schritte ?? []).forEach((r) => map.set(r.schritt, r));
    return map;
  }, [dash?.schritte]);

  const steps: PmStep[] = useMemo(() => {
    // Phasen-Aggregate fuer Gating: Eine Phase wird aktiv, sobald die Vorphase
    // einen Durchschnitt >= 3 erreicht ODER in der Phase selbst bereits gearbeitet
    // wurde (irgendein Schritt mit Reifegrad > 0). Die Schritt-Reihenfolge ist
    // damit NICHT zwingend — wer direkt eine Anforderung (Planung) oder einen
    // Ausfuehrungs-Schritt bearbeitet, aktiviert die jeweilige Phase sofort.
    const initLevels = [1, 2, 3, 4, 5, 6, 7].map((n) => reifegradMap.get(n)?.reifegrad ?? 0);
    const planLevels = [8, 9, 10, 11, 12, 13].map((n) => reifegradMap.get(n)?.reifegrad ?? 0);
    const execLevels = [14, 15, 16, 17].map((n) => reifegradMap.get(n)?.reifegrad ?? 0);
    const avg = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
    const touched = (a: number[]) => a.some((l) => l > 0);
    const planActive = avg(initLevels) >= 3 || touched(planLevels);
    const execActive = (planActive && avg(planLevels) >= 3) || touched(execLevels);

    return STEPS.map((s) => {
      const r = reifegradMap.get(s.number);
      const level = r?.reifegrad ?? 0;
      const phaseActive =
        s.number <= 7 ? true :
        s.number <= 13 ? planActive :
        execActive;
      const maturity = (level / 5) * 100;
      const status: PmStep['status'] = !phaseActive ? 'idle'
        : level >= 4 ? 'done'
        : level >= 2 ? 'wip'
        : 'open'; // level 0 oder 1 in aktiver Phase = offen/rot
      return { ...s, maturity, status };
    });
  }, [reifegradMap]);

  const reifegradAvg = useMemo(() => {
    const values = (dash?.schritte ?? [])
      .map((s) => s.reifegrad)
      .filter((v): v is number => typeof v === 'number');
    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [dash?.schritte]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // "Noch nicht fertig" = open (rot) ODER wip (gelb). idle (grau) bedeutet
  // "Phase noch nicht erreicht" und wird übersprungen, bis das Phase-Gate offen ist.
  const nextStep = steps.find((s) => s.status === 'open' || s.status === 'wip');
  const kpi = dash?.kpi ?? {};
  const latestWeek = (dash?.weeklyProgress ?? [])[0] ?? null;
  const weekTotal = (latestWeek?.erledigt ?? 0) + (latestWeek?.offen ?? 0);

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('dashboard.title', 'Dashboard') };
  const buildReport = () =>
    buildDashboardReport({
      reifegradAvg,
      risksActive: kpi.rActive ?? 0,
      offenePunkteOpen: kpi.opOpen ?? 0,
      latestWeek: latestWeek
        ? {
            kw: latestWeek.kw ?? null,
            jahr: latestWeek.jahr ?? null,
            erledigt: latestWeek.erledigt ?? 0,
            offen: latestWeek.offen ?? 0,
          }
        : null,
      nextStep: nextStep ? { number: nextStep.number, label: nextStep.label } : null,
    });

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h6">{t('dashboard.title', 'Dashboard')}</Typography>
        <PdfExportButton meta={pdfMeta} build={buildReport} />
      </Stack>
      {/* 1 — Schritt-Indikatoren nach 17-Schritte-Framework */}
      <PmCard>
        <SectionHeader
          title={t('dashboard.frameworkProgress', 'Projekt-Fortschritt nach 17-Schritte-Framework')}
          action={reifegradAvg != null ? <PmPill tone="primary">Ø {reifegradAvg.toFixed(1)} / 5</PmPill> : undefined}
        />
        <DashboardStepIndicators
          steps={steps}
          onSelect={(s) => navigate(`/projects/${projectId}/${s.key}`)}
        />
      </PmCard>

      {/* 2 — KPI-Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <PmCard>
          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('dashboard.risks', 'Risiken aktiv')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{kpi.rActive ?? 0}</Typography>
            <Box
              component="a"
              onClick={() => navigate(`/projects/${projectId}/risiken`)}
              sx={{ cursor: 'pointer', color: 'var(--pm-fg-accent)', fontSize: '0.75rem', textDecoration: 'underline' }}
            >
              → Risiken öffnen
            </Box>
          </Stack>
        </PmCard>

        <PmCard>
          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('dashboard.openPoints', 'Offene Punkte')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{kpi.opOpen ?? 0}</Typography>
            <Box
              component="a"
              onClick={() => navigate(`/projects/${projectId}/offene-punkte`)}
              sx={{ cursor: 'pointer', color: 'var(--pm-fg-accent)', fontSize: '0.75rem', textDecoration: 'underline' }}
            >
              → Offene Punkte öffnen
            </Box>
          </Stack>
        </PmCard>

        <PmCard>
          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('dashboard.weeklyProgress', 'Wochenfortschritt')}
            </Typography>
            {latestWeek ? (
              <>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{latestWeek.erledigt ?? 0}</Typography>
                <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
                  KW {latestWeek.kw}/{latestWeek.jahr} · {latestWeek.offen ?? 0} offen
                </Typography>
                {weekTotal > 0 && (
                  <ProgressBar
                    label={t('dashboard.done', 'Erledigt')}
                    value={latestWeek.erledigt ?? 0}
                    total={weekTotal}
                    color="var(--pm-success)"
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)' }}>
                {t('dashboard.noWeekly', 'Noch keine Wochenmeldung')}
              </Typography>
            )}
          </Stack>
        </PmCard>
      </Box>

      {/* 3 — Arbeitsfortschritt + Mini-Gantt */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
        <PmCard>
          <SectionHeader title={t('dashboard.workProgress', 'Arbeitsfortschritt')} />
          <DashboardWorkChart projectId={projectId} />
        </PmCard>
        <DashboardMiniGantt projectId={projectId} />
      </Box>

      {/* 4 — Nächste Meilensteine + Letzte Aktivitäten */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
        <PmCard>
          <SectionHeader title={t('dashboard.nextMilestones', 'Nächste Meilensteine')} />
          <DashboardMilestones projectId={projectId} />
        </PmCard>
        <PmCard>
          <SectionHeader title={t('dashboard.recentActivity', 'Letzte Aktivitäten')} />
          <Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)', py: 1 }}>
            {t('dashboard.activityPending', 'Aktivitäts-Feed folgt — Backend-Endpoint in Vorbereitung.')}
          </Typography>
        </PmCard>
      </Box>

      {/* 5 — Geführter nächster Schritt */}
      {nextStep && (
        <PmInfoBox variant="info" title={t('dashboard.guidedNextStep', 'Geführter nächster Schritt')}>
          <Stack spacing={1.5}>
            <Box>
              Schritt {nextStep.number}: <strong>{nextStep.label}</strong>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/projects/${projectId}/${nextStep.key}`)}
              >
                {t('dashboard.toStep', 'Zu {{label}}', { label: nextStep.label })}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<BuildIcon />}
                onClick={() => navigate('/werkzeuge')}
              >
                {t('dashboard.openToolbox', 'Werkzeugkoffer öffnen')}
              </Button>
            </Stack>
          </Stack>
        </PmInfoBox>
      )}
    </Stack>
  );
}
